'use client';

import React, { forwardRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import Box from '../../atoms/Box/Box';
import Typography from '../../atoms/Typography/Typography';
import IconButton from '../../atoms/IconButton/IconButton';
import styles from './Modal.module.scss';

/**
 * Theme options for the Modal component
 */
type Theme = 'dark' | 'light';

/**
 * Size options for the Modal component
 */
type ModalSize = 'small' | 'medium' | 'large';

/**
 * Props for the Modal component
 */
export type ModalProps = {
  /** Test ID for the modal container */
  dataTestId: string;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback function when the modal is closed */
  onClose: () => void;
  /** Title displayed in the modal header */
  title: string;
  /** Theme variant of the modal */
  theme?: Theme;
  /** Size variant of the modal */
  size?: ModalSize;
  /** Whether to show the back button in the header */
  showBackButton?: boolean;
  /** Callback function when the back button is clicked */
  onBack?: () => void;
  /** Content to render in the modal body */
  children: React.ReactNode;
  /** Content to render in the modal footer */
  footer?: React.ReactNode;
  /** Whether clicking the overlay closes the modal */
  closeOnOverlayClick?: boolean;
  /** Whether pressing Escape closes the modal */
  closeOnEscape?: boolean;
  /** Additional class name for the modal */
  className?: string;
};

/**
 * Modal component that displays content in an overlay dialog
 *
 * @component
 * @param {ModalProps} props - The component props
 * @returns {React.ReactElement | null} The rendered Modal component
 *
 * @example
 * ```tsx
 * <Modal
 *   dataTestId="example-modal"
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="Modal Title"
 *   theme="dark"
 *   footer={<Button>Submit</Button>}
 * >
 *   <p>Modal content goes here</p>
 * </Modal>
 * ```
 */
const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      dataTestId,
      isOpen,
      onClose,
      title,
      theme = 'dark',
      size = 'medium',
      showBackButton = false,
      onBack,
      children,
      footer,
      closeOnOverlayClick = true,
      closeOnEscape = true,
      className,
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState<boolean>(false);

    // Handle animation timing
    useEffect(() => {
      if (isOpen) {
        setIsVisible(true);
      } else {
        const timeout = setTimeout(() => setIsVisible(false), 200);
        return () => clearTimeout(timeout);
      }
      return undefined;
    }, [isOpen]);

    // Handle escape key press
    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (closeOnEscape && event.key === 'Escape') {
          onClose();
        }
      },
      [closeOnEscape, onClose]
    );

    useEffect(() => {
      if (isOpen) {
        document.addEventListener('keydown', handleKeyDown);
      }
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [isOpen, handleKeyDown]);

    // Disable body scroll when modal is open
    useEffect(() => {
      if (isVisible) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
      return () => {
        document.body.style.overflow = '';
      };
    }, [isVisible]);

    // Handle overlay click
    const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (closeOnOverlayClick && event.target === event.currentTarget) {
        onClose();
      }
    };

    if (!isOpen && !isVisible) return null;

    const modalContent = (
      <Box
        ref={ref}
        className={classnames(styles.overlay, {
          [styles.enter]: isVisible && isOpen,
          [styles.exit]: !isOpen,
        })}
        data-testid={`${dataTestId}-overlay`}
        data-theme={theme}
        onClick={handleOverlayClick}
      >
        <Box
          className={classnames(
            styles.modal,
            styles[`size-${size}`],
            {
              [styles.enter]: isVisible && isOpen,
              [styles.exit]: !isOpen,
            },
            className
          )}
          data-testid={dataTestId}
          data-theme={theme}
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${dataTestId}-title`}
        >
          {/* Header */}
          <Box className={styles.header} data-testid={`${dataTestId}-header`}>
            <Box className={styles.headerLeft}>
              {showBackButton && (
                <IconButton
                  icon={{ category: 'arrows', name: 'chevron-left' }}
                  size="small"
                  theme={theme}
                  buttonType="tertiary"
                  gaComponent="modal-back"
                  dataTestId={`${dataTestId}-back-button`}
                  handleClick={onBack ?? onClose}
                />
              )}
            </Box>
            <Box className={styles.headerCenter}>
              <Typography
                variant="subtitle"
                size="small"
                theme={theme}
                color="white"
                id={`${dataTestId}-title`}
              >
                {title}
              </Typography>
            </Box>
            <Box className={styles.headerRight}>
              <IconButton
                icon={{ category: 'global', name: 'close' }}
                size="small"
                theme={theme}
                buttonType="tertiary"
                gaComponent="modal-close"
                dataTestId={`${dataTestId}-close-button`}
                handleClick={onClose}
              />
            </Box>
          </Box>

          {/* Body */}
          <Box className={styles.body} data-testid={`${dataTestId}-body`}>
            {children}
          </Box>

          {/* Footer */}
          {footer && (
            <Box className={styles.footer} data-testid={`${dataTestId}-footer`}>
              {footer}
            </Box>
          )}
        </Box>
      </Box>
    );

    // Use portal to render modal at document.body level for highest z-index stacking
    if (typeof document !== 'undefined') {
      return createPortal(modalContent, document.body);
    }

    return modalContent;
  }
);

Modal.displayName = 'Modal';

export default Modal;
